import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { COLLECTION_URL, PRODUCTS, TRUST } from "@/lib/site";

const card = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Compact = () => (
  <section
    id="kit"
    data-testid="compact-section"
    className="relative flex min-h-screen flex-col justify-between border-t border-[#262626] bg-[#0a0a0b] px-5 py-12 sm:px-8 sm:py-14"
  >
    {/* Header row */}
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-5 md:flex-row md:items-end md:justify-between">
      <div>
        <div className="mb-3 flex items-center gap-3">
          <span className="h-px w-10 bg-[#ee1111]" />
          <span className="font-cond text-xs font-semibold uppercase tracking-[0.3em] text-[#ee1111] sm:text-sm">
            The Kit — 26 Products
          </span>
        </div>
        <h2 className="font-display text-4xl uppercase leading-[0.9] text-white sm:text-5xl">
          Engineered for the wild
        </h2>
      </div>
      <a
        data-testid="compact-shop-btn"
        href={COLLECTION_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="group inline-flex w-fit items-center gap-3 rounded-full bg-[#ee1111] px-7 py-4 font-cond text-sm font-bold uppercase tracking-[0.18em] text-white transition-all duration-300 hover:scale-[1.03] hover:bg-[#cc0f0f] hover:shadow-[0_0_45px_-10px_#ee1111]"
      >
        Shop the Collection
        <ArrowUpRight
          size={18}
          className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
        />
      </a>
    </div>

    {/* Product row */}
    <motion.div
      variants={{ show: { transition: { staggerChildren: 0.05 } } }}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      className="mx-auto grid w-full max-w-[1400px] grid-cols-2 gap-3 md:grid-cols-5"
    >
      {PRODUCTS.map((p, i) => (
        <motion.a
          key={p.id}
          variants={card}
          custom={i}
          data-testid={`product-card-${p.id}`}
          href={COLLECTION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative flex aspect-[4/5] overflow-hidden rounded-xl border border-[#262626] bg-[#0f0f10] transition-colors duration-500 hover:border-[#ee1111]/60"
        >
          <img
            src={p.img}
            alt={p.title}
            className="absolute inset-0 h-full w-full object-cover opacity-70 transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-95"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/30 to-transparent" />
          <div className="relative z-10 flex w-full flex-col justify-between p-4">
            <span className="font-cond text-xs font-semibold tracking-[0.2em] text-[#ee1111]">
              {p.n}
            </span>
            <div className="flex items-center gap-1.5">
              <h3 className="font-display text-lg uppercase leading-none text-white sm:text-xl">
                {p.title}
              </h3>
              <ArrowUpRight
                size={16}
                className="shrink-0 text-white/60 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#ee1111]"
              />
            </div>
          </div>
        </motion.a>
      ))}
    </motion.div>

    {/* Trust strip + footer */}
    <div className="mx-auto w-full max-w-[1400px]">
      <div className="grid grid-cols-2 border-l border-t border-[#262626] lg:grid-cols-4">
        {TRUST.map((t) => (
          <div
            key={t.n}
            data-testid={`trust-cell-${t.n}`}
            className="border-b border-r border-[#262626] px-5 py-4 transition-colors duration-500 hover:bg-[#0f0f10]"
          >
            <div className="flex items-baseline gap-2">
              <span className="font-cond text-2xl font-bold leading-none text-[#ee1111]">
                {t.n}
              </span>
              <h3 className="font-display text-base uppercase leading-none text-white sm:text-lg">
                {t.title}
              </h3>
            </div>
            <p className="mt-1.5 font-cond text-xs uppercase tracking-[0.08em] text-white/60">
              {t.metric}
            </p>
          </div>
        ))}
      </div>

      <footer className="mt-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
        <img
          src="/oya-logo.png"
          alt="Own Your Adventure"
          className="h-7 w-auto"
          draggable={false}
        />
        <p className="font-cond text-xs uppercase tracking-[0.2em] text-white/40">
          © {new Date().getFullYear()} Own Your Adventure · Curated by Motousher
        </p>
      </footer>
    </div>
  </section>
);
